import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Paper, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import _ from "underscore";
import { bookingDataValueState, customerDataValueState, otpValidationValueState, stepState } from "../state";
import { ICustomerData, IOtpValidationForm } from "../../models/models";
import { OTP_VALIDATION_DATA_FORM_INITIAL_VALUES } from "./config";
import OtpValidationData from "./otp-validation-data/OtpValidationData";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { fetchVerifyEmailMobileOtp } from "../services/http.services";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { deepClone } from "../../../../util/state/commonFunctions";
import CustomLoader from "../../../../util/component/CustomLoader";

const OtpValidation = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);

	const [nextDisableStatus, setNextDisableStatus] = useState<boolean>(false);
	const [step, setStep] = useRecoilState(stepState);
	const customerDataValue = useRecoilValue(customerDataValueState);
	const bookingDataValue: any = useRecoilValue(bookingDataValueState);

	const [optValueState, setOptValueState] = useRecoilState(otpValidationValueState);

	// Use Context
	const { dispatchGlobalData } = useContext(GlobalContext);

	const otpValidationForm = useForm<IOtpValidationForm>({
		defaultValues: OTP_VALIDATION_DATA_FORM_INITIAL_VALUES
	})

	let verifyEmailMobilePayload: any = null;

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (step.active > 0) {

			setStep({
				active: step.active - 1,
				completed: step.completed,
				numSteps: step.numSteps
			});
		}
	}

	const handleNextStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		if (nextDisableStatus) {
			// set next step
			if (step.active < (step.numSteps - 1)) {
				const completed = step.completed.concat();
				completed[step.active + 1] = true;
				setStep({
					active: step.active + 1,
					completed,
					numSteps: step.numSteps
				});
			}
		}
	}

	const handleSkipValidation = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		setNextDisableStatus(true);

		// todo integrare WRest per richiesta otp & conferma OTP: invio idRentalAgreement, customerID, customerType, typo campo da validare -> email || phone
	}

	const handleValidationDriver = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		setNextDisableStatus(true)
	}

	/**  Fetch Verify email & mobile */
	const fetchVerifyEmailMobileQuery = useQuery(['fetchVerifyEmailMobileOtp', verifyEmailMobilePayload], () => fetchVerifyEmailMobileOtp(verifyEmailMobilePayload), {
		enabled: false,
		onSuccess: (response: any) => {
			const optVSt = deepClone(optValueState);

			// main driver
			optVSt.mainDriver.email = response.mainDriverIsEmailCertified;
			optVSt.mainDriver.mobile = response.mainDriverIsMobileCertified;
			let nextButtonStatus = optVSt.mainDriver.email && optVSt.mainDriver.mobile;

			//first dirver
			if (!!response.firstDriverCbId) {
				optVSt.additionalDrivers[0] = {
					email: response.firstDriverIsEmailCertified,
					mobile: response.firstDriverIsMobileCertified
				}
				nextButtonStatus = nextButtonStatus && optVSt.additionalDrivers[0].email && optVSt.additionalDrivers[0].mobile;
			}

			// second driver
			if (!!response.secondDriverCbId) {
				optVSt.additionalDrivers[1] = {
					email: response.secondDriverIsEmailCertified,
					mobile: response.secondDriverIsMobileCertified
				}

				nextButtonStatus = nextButtonStatus && optVSt.additionalDrivers[1].email && optVSt.additionalDrivers[1].mobile;
			}

			if (!nextButtonStatus) {
				dispatchGlobalData({type: setSnackbar,
					payload: {
						snackbar: {
							message: t('otpValidation.message.warning.toValidate'),
							type: snackbarTypes.info,
							show: true,
							autoClose: true,
						}
					}
				});
			}

			setOptValueState(optVSt);
			setNextDisableStatus(nextButtonStatus);
		},
		onError: (error: AxiosError) => {
			dispatchGlobalData({type: setSnackbar,
				payload: {
					snackbar: {
						message: t('translation:error.tryLater'),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					}
				}
			});
		}
	})

	// use effects customerDataValue.mainDriver.name
	useEffect(() => {
		if (!_.isEmpty(customerDataValue)) {
			otpValidationForm.setValue('mainDriver.name', customerDataValue.mainDriver.name);
			otpValidationForm.setValue('mainDriver.surname', customerDataValue.mainDriver.surname);
			otpValidationForm.setValue('mainDriver.email', customerDataValue.mainDriver.email);
			// @ts-ignore
			otpValidationForm.setValue('mainDriver.mobile', '+' + customerDataValue.mainDriver?.prefix?.description + customerDataValue.mainDriver.mobile);

			customerDataValue.additionalDrivers
				.map((addDr: any, index: number) => {
					otpValidationForm.setValue(`additionalDrivers.${index}.name`, addDr.name);
					otpValidationForm.setValue(`additionalDrivers.${index}.surname`, addDr.surname);
					otpValidationForm.setValue(`additionalDrivers.${index}.email`, addDr.email);
					// @ts-ignore
					otpValidationForm.setValue(`additionalDrivers.${index}.mobile`, '+' + addDr.prefix.description + addDr.mobile);
					return addDr;
				});
		}

		verifyEmailMobilePayload = {
			idRentalAgreement:		bookingDataValue.idRentalAgreement,
			mainDriverCbId: 		bookingDataValue.raOwner.cbId.toString(),
			mainDriverEmail:		bookingDataValue.raOwner.email,
			mainDriverMobile:		'+' + bookingDataValue.raOwner.prefix.description + bookingDataValue.raOwner.mobile,
			mainDriverFullName:		bookingDataValue.raOwner.firstName + ' ' + bookingDataValue.raOwner.lastName,

		};

		if (!!customerDataValue.additionalDrivers[0]) {
			verifyEmailMobilePayload['firstDriverCbId'] =		bookingDataValue.firstDriver.cbId.toString();
			verifyEmailMobilePayload['firstDriverEmail'] =		bookingDataValue.firstDriver.email;
			verifyEmailMobilePayload['firstDriverMobile'] =		'+' + bookingDataValue.firstDriver.prefix.description + bookingDataValue.firstDriver.mobile;
			verifyEmailMobilePayload['firstDriverFullName'] = 	bookingDataValue.firstDriver.firstName + ' ' + bookingDataValue.firstDriver.lastName;
		}

		if (!!customerDataValue.additionalDrivers[1]) {
			verifyEmailMobilePayload['secondDriverCbId'] =		bookingDataValue.driver.cbId.toString();
			verifyEmailMobilePayload['secondDriverEmail'] =		bookingDataValue.driver.email;
			verifyEmailMobilePayload['secondDriverMobile'] =	'+' + bookingDataValue.driver.prefix.description + bookingDataValue.driver.mobile;
			verifyEmailMobilePayload['secondDriverFullName'] = 	bookingDataValue.driver.firstName + ' ' + bookingDataValue.driver.lastName;
		}

		fetchVerifyEmailMobileQuery.refetch();
	}, [])

	return (
		<>
			<FormProvider {...otpValidationForm}>
				<OtpValidationData
					title={t('otpValidation.form.title.mainDriver')}
					referenceProp={'mainDriver'}
				/>

				{
					!!customerDataValue.additionalDrivers &&
					customerDataValue.additionalDrivers
						.map((driver: ICustomerData, index: number) =>
							<OtpValidationData
								key={`driver-additional${index}`}
								title={t('otpValidation.form.title.additionalDriver')}
								referenceProp={'additionalDrivers'}
								ind={index}
							/>
						)
				}

				<Box sx={{display: 'flex',  justifyContent: 'center', mt: theme.spacing(4)}}>
					<Button	color={"accent"}
							   onClick={handleSkipValidation}
							   sx={{mx: theme.spacing(1)}}>
						{t('otpValidation.form.button.skipValidation')}
					</Button>

					<Button	color={"accent"}
							   onClick={handleValidationDriver}
							   sx={{mx: theme.spacing(1)}}>
						{t('otpValidation.form.button.confirmValidDrivers')}
					</Button>
				</Box>
			</FormProvider>

			<Box sx={{display: 'flex',  justifyContent: 'space-between', mt: theme.spacing(2)}}>
				{step.active === 0 ? <Box></Box> : <Button variant={'text'} color={"primary"}  onClick={handleBackStep}>{t('common.button.backStep')}</Button>}
				{step.active ===  (step.numSteps -1) ?
					<Box></Box> :
					<Button variant={'outlined'} color={"accent"}
							onClick={handleNextStep}
							disabled={!nextDisableStatus}>
						{t('common.button.nextStep')}
					</Button>}
			</Box>

			<CustomLoader loading={fetchVerifyEmailMobileQuery.isFetching} />
		</>
	)
}

export default OtpValidation;
